import { useEffect, useState } from "react";
import { useOutletContext, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ShieldCheck, TruckIcon, FlaskConical, ArrowRight } from "lucide-react";
import PillarsStrip from "@/components/tex/PillarsStrip";
import ClientMarquee from "@/components/tex/ClientMarquee";
import PincodeLookup from "@/components/tex/PincodeLookup";
import { staggerContainer, staggerItem } from "@/components/tex/PageTransition";
import { api } from "@/api";

export default function HomePage() {
  const { company, openQuote } = useOutletContext();
  const [pillars, setPillars] = useState([]);
  const [clients, setClients] = useState([]);
  const [products, setProducts] = useState([]);

  useEffect(() => {
    document.title = `${company?.name || "Texcheme"} — Your Chemicals. Our Delivery.`;
  }, [company]);

  useEffect(() => {
    let alive = true;
    api.getSiteContent()
      .then((site) => {
        if (!alive) return;
        setPillars(site.pillars || []);
        setClients(site.clients || []);
        setProducts(site.products || []);
      })
      .catch(() => {});
    return () => { alive = false; };
  }, []);

  return (
    <>
      {/* ---------------- HERO ---------------- */}
      <header className="hero-light" data-testid="hero">
        <motion.div
          className="wrap hero-light-grid"
          variants={staggerContainer}
          initial="hidden"
          animate="visible"
        >
          <div>
            <motion.div className="eyebrow" variants={staggerItem}>
              Texcheme · Guardian-Sage
            </motion.div>
            <motion.h1 variants={staggerItem} className="hero-light-title">
              Your Chemicals.
              <br />
              <span className="hero-light-accent">Our Delivery.</span>
            </motion.h1>
            <motion.p className="hero-light-lead" variants={staggerItem}>
              Texcheme specializes in the safe and reliable transportation of industrial
              chemicals across India — bulk road-tanker supply of acids, alkalis, sulphates,
              nitrates and specialty salts. Trained drivers, batch-wise COAs, and pan-India
              dispatch from six warehouses across South &amp; West India (Coimbatore, Chennai,
              Cochin, Bangalore, Dahej, Mumbai).
            </motion.p>
            <motion.div className="hero-light-cta" variants={staggerItem}>
              <button
                className="btn btn-brand"
                onClick={() => openQuote("hero")}
                data-testid="hero-request-quote"
              >
                Request a quote <ArrowRight size={16} strokeWidth={2.5} />
              </button>
              <Link
                to="/tools"
                className="btn btn-ghost-dark"
                data-testid="hero-open-tools"
              >
                Safety toolbelt
              </Link>
            </motion.div>
            <motion.div className="hero-light-facts" variants={staggerItem}>
              {company?.stats?.slice(0, 4).map((s) => (
                <div key={s.label}>
                  <div className="hero-light-stat">{s.value}</div>
                  <div className="hero-light-statlabel">{s.label}</div>
                </div>
              ))}
            </motion.div>
          </div>
          <motion.div className="hero-light-art" variants={staggerItem} aria-hidden>
            <img src="/texcheme-logo.png" alt="" className="hero-light-mark" />
          </motion.div>
        </motion.div>
      </header>

      {/* ---------------- PILLARS ---------------- */}
      <PillarsStrip pillars={pillars} onCta={(k) => openQuote(`pillar-${k}`)} />

      {/* ---------------- PRODUCTS PREVIEW ---------------- */}
      <section className="section-lg products-band" data-testid="products-band">
        <div className="wrap">
          <div className="section-eyebrow">Our chemicals</div>
          <div className="products-band-head">
            <h2 style={{ maxWidth: "22ch" }}>25+ SKUs across 18 industrial chemicals — supplied in bulk.</h2>
            <p className="lead" style={{ maxWidth: "48ch" }}>
              A preview of what we deliver — acids, alkalis, sulphates, nitrates,
              hypochlorites and specialty salts. Full tanker loads, tonne-level pricing
              and batch-wise COAs on every dispatch. See the full catalogue below.
            </p>
          </div>
          <div className="products-grid" data-testid="products-grid">
            {products.slice(0, 6).map((p, i) => {
              const testId = `product-${(p.key || p.name).toLowerCase().replace(/[^a-z0-9]+/g, "-")}-${i}`;
              return (
                <div
                  key={testId}
                  className={"product-card" + (p.has_tools ? " has-tools" : "")}
                  data-testid={testId}
                >
                  <div
                    className="product-formula"
                    style={{ background: p.color + "18", color: p.color }}
                  >
                    {p.formula}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div className="product-name">{p.name}</div>
                    <div className="product-grades">{p.grades?.join(" · ")}</div>
                    {p.category && <div className="product-category">{p.category}</div>}
                  </div>
                  {p.has_tools && (
                    <Link
                      to="/tools"
                      className="product-tools-badge"
                      data-testid={`${testId}-tools`}
                      title="Safety & compliance data available"
                    >
                      Tools →
                    </Link>
                  )}
                </div>
              );
            })}
          </div>
          <div style={{ marginTop: 28, display: "flex", gap: 12, flexWrap: "wrap" }}>
            <Link to="/products" className="btn btn-brand" data-testid="products-show-full-list">
              Show full list ({products.length} chemicals) <ArrowRight size={16} strokeWidth={2.5} />
            </Link>
            <Link to="/tools" className="btn btn-ghost-dark" data-testid="products-view-tools">
              View safety &amp; compliance data
            </Link>
          </div>
        </div>
      </section>

      {/* ---------------- WHY TEXCHEME (values band) ---------------- */}
      <section className="section-lg why-band" data-testid="why-band">
        <div className="wrap">
          <div className="section-eyebrow">Why Texcheme</div>
          <div className="why-grid">
            <div className="why-card">
              <div className="why-icon"><TruckIcon size={22} strokeWidth={2} /></div>
              <h3>Dispatch you can plan on</h3>
              <p>Trained drivers, state-of-the-art tankers, and a multi-depot network minimise transit time — every shipment shows up when we said it would.</p>
            </div>
            <div className="why-card">
              <div className="why-icon"><FlaskConical size={22} strokeWidth={2} /></div>
              <h3>Paperwork that clears QC</h3>
              <p>Batch-wise COAs and current-revision SDS on every consignment. Your quality team never chases us for documents.</p>
            </div>
            <div className="why-card">
              <div className="why-icon"><ShieldCheck size={22} strokeWidth={2} /></div>
              <h3>Safety as a shared job</h3>
              <p>The same first-aid, PPE, spill and compatibility references our own team uses — made free for any plant that runs our chemicals.</p>
            </div>
          </div>
        </div>
      </section>

      {/* ---------------- PINCODE LOOKUP ---------------- */}
      <PincodeLookup />

      {/* ---------------- CERTIFICATIONS BAND ---------------- */}
      <section className="section-lg certs-band" data-testid="certs-band">
        <div className="wrap certs-grid">
          <div>
            <div className="section-eyebrow">Certifications & standards</div>
            <h2 style={{ maxWidth: "20ch" }}>Grades that meet BIS, ISO and FSSAI.</h2>
            <p className="lead" style={{ maxWidth: "48ch" }}>{company?.certifications_note}</p>
          </div>
          <ul className="certs-list">
            <li><span className="certs-dot" /> BIS-licensed grades (IS 251/252/264/265/266/4835)</li>
            <li><span className="certs-dot" /> ISO 9001:2015 quality management</li>
            <li><span className="certs-dot" /> FSSAI-registered food-grade lines</li>
            <li><span className="certs-dot" /> Batch-wise COA on every dispatch</li>
          </ul>
        </div>
      </section>

      {/* ---------------- TRUSTED BY ---------------- */}
      <ClientMarquee clients={clients} />

      {/* ---------------- CTA ---------------- */}
      <section className="panel cta-panel" data-testid="cta-panel">
        <div className="wrap cta-panel-inner">
          <div>
            <h2>Ready to place a standing order?</h2>
            <p>Tell us your monthly tonnage and we&apos;ll get bulk-tanker pricing back the same day.</p>
          </div>
          <button
            className="btn btn-brand"
            onClick={() => openQuote("cta-panel")}
            data-testid="cta-request-quote"
          >
            Request a quote
          </button>
        </div>
      </section>
    </>
  );
}
